/**
 * Keeping DeskWorks One current without a trip to the website.
 *
 * - Web: the server tags each web build (`<meta name="dw-web-build">` in the page it served, and
 *   /v1/web/version for the newest). When a newer build is deployed, open tabs and desktop windows
 *   offer a reload. Nothing reloads by itself, so a draft or a call is never lost.
 * - Desktop app: the app downloads updates in the background and reports through the bridge; when
 *   one is ready the same bar offers a restart. Settings › About & updates checks on demand.
 */
import { Button, Icon, toast } from '@deskworks/ui';
import { useCallback, useEffect, useState } from 'react';
import { desktop, type DesktopUpdateState } from './platform.js';
import { unsavedWork } from './session.js';

/** This tab's web build (null on the development server). */
export const OWN_BUILD = typeof document !== 'undefined' ? (document.querySelector('meta[name="dw-web-build"]')?.getAttribute('content') ?? null) : null;
const POLL = 15 * 60_000;
const MIN_GAP = 2 * 60_000;

/** The newest web build on the server, or null when it can't be told (offline, development). */
export async function latestWebBuild(): Promise<string | null> {
  try {
    const res = await fetch('/v1/web/version', { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return null;
    return ((await res.json()) as { build?: string }).build ?? null;
  } catch {
    return null;
  }
}

/** True once the server has a newer web build than the one running in this tab. */
export function useNewWebBuild(): boolean {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    if (!OWN_BUILD) return; // Development server: no build id.
    let last = 0;
    let stopped = false;
    const check = async () => {
      if (stopped || Date.now() - last < MIN_GAP) return;
      last = Date.now();
      const build = await latestWebBuild();
      if (build && build !== OWN_BUILD) setNewer(true);
    };
    const onVisible = () => document.visibilityState === 'visible' && void check();
    const timer = window.setInterval(() => void check(), POLL);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, []);
  return newer;
}

/** The desktop app's update state, or null in a browser (or an older desktop app). */
export function useDesktopUpdate() {
  const updates = desktop?.updates ?? null;
  const [state, setState] = useState<DesktopUpdateState | null>(null);
  useEffect(() => {
    if (!updates) return;
    void updates.state().then((s) => s && setState(s));
    return updates.onChange(setState);
  }, [updates]);
  const check = useCallback(async () => {
    if (!updates) return;
    setState({ status: 'checking' });
    const s = await updates.check();
    if (s) setState(s);
  }, [updates]);
  return { supported: !!updates, state, check, restart: () => updates?.restart() };
}

export function reloadForUpdate() {
  if (unsavedWork().length) {
    toast('Send or save what you’re writing first, then reload.', { tone: 'warning' });
    return;
  }
  window.location.reload();
}

/** A slim bar at the top of the app when a newer version is ready. */
export function UpdateBar() {
  const newWeb = useNewWebBuild();
  const app = useDesktopUpdate();
  const [dismissed, setDismissed] = useState<string | null>(null);
  const appReady = app.state?.status === 'ready' ? app.state.version : null;
  const key = appReady ? `app:${appReady}` : newWeb ? 'web' : null;
  if (!key || dismissed === key) return null;
  return (
    <div className="dw-updatebar" role="status">
      <Icon name="download" size={15} />
      <span className="dw-updatebar__text">
        {appReady ? `DeskWorks One ${appReady} is ready to install.` : 'A new version of DeskWorks One is available.'}
      </span>
      <Button size="sm" variant="primary" onClick={() => (appReady ? app.restart() : reloadForUpdate())}>
        {appReady ? 'Restart now' : 'Reload'}
      </Button>
      <Button size="sm" variant="plain" onClick={() => setDismissed(key)}>
        Later
      </Button>
    </div>
  );
}
